import { InmateDevice } from '@reliance/types/models/InmateDevice';
import { Inmate } from '@reliance/types/models/Inmate';
import moment from 'moment';
import React, { useCallback, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { Button, Card, Col, Row } from 'react-bootstrap';
import { formatPhoneNumber } from '../../format/PhoneNumber';
import { useInmateDevicesByInmateId } from '../../hooks/InmateDevice';
import { useTabLazyLoad } from '../../hooks/useTabLazyLoad';
import { useNotifyTabLoading } from '../../hooks/useTabLoadingReporter';
import BaseHtmlTable from '../../table/BaseHtmlTable';
import { createColumnHelper } from '@tanstack/react-table';
import InmateDeviceBlacklistAdd from '../../inmate-device-blacklist/InmateDeviceBlacklistAdd';

type InmateDevicesProps = {
  inmate: Inmate;
  tabSelected?: boolean;
  refreshTrigger?: number;
  onBlacklistChange?: () => void;
};

const InmateDevices: React.FC<InmateDevicesProps> = (props) => {
  const { inmate, tabSelected, refreshTrigger, onBlacklistChange } = props;

  const filters = useMemo(() => ({
    inmateId: inmate.id,
    includeUnassigned: true,
    page: 1,
    perPage: 100,
    orderBy: 'unassignDate,-assignDate',
  }), [inmate.id]);

  const devicesResult = useInmateDevicesByInmateId(null, filters);

  useTabLazyLoad({
    key: inmate.id,
    tabSelected,
    refreshTrigger,
    execute: useCallback(() => {
      if (inmate.id) {
        void devicesResult.execute(inmate.id);
      }
    }, [devicesResult, inmate.id]),
  });

  useNotifyTabLoading('devices', devicesResult.loading);

  const columnHelper = createColumnHelper<InmateDevice>();
  const columns = useMemo(() => [
    columnHelper.accessor((row) => {
      return `${row.inmate.firstName} ${row.inmate.lastName}`;
    }, {
      id: 'inmate',
      header: 'Inmate',
      enableSorting: true,
    }),
    columnHelper.accessor((row) => {
      const date = typeof row.assignDate === 'object' && row.assignDate.date
        ? moment(row.assignDate.date)
        : moment(row.assignDate as any);
      return date.format('YYYY/MM/DD HH:mm:ss');
    }, {
      id: 'assignDate',
      header: 'Assigned',
      enableSorting: true,
    }),
    columnHelper.accessor((row) => {
      if (row.unassignDate) {
        const date = typeof row.unassignDate === 'object' && row.unassignDate.date
          ? moment(row.unassignDate.date)
          : moment(row.unassignDate as any);
        return date.format('YYYY/MM/DD HH:mm:ss');
      }
      return '';
    }, {
      id: 'unassignDate',
      header: 'Unassigned',
      enableSorting: true,
    }),
    columnHelper.accessor((row) => {
      if (!row.unassignDate && row.nextRentalCharge) {
        const date = typeof row.nextRentalCharge === 'object' && row.nextRentalCharge.date
          ? moment(row.nextRentalCharge.date)
          : moment(row.nextRentalCharge as any);
        return date.format('MM/DD/YYYY');
      }
      return '';
    }, {
      id: 'nextRentalCharge',
      header: 'Next Rental Due',
      enableSorting: true,
    }),
    columnHelper.accessor((row) => {
      return row.smsNumber ? formatPhoneNumber(row.smsNumber.number) : '';
    }, {
      id: 'ani',
      header: 'ANI',
      enableSorting: true,
    }),
    columnHelper.accessor((row) => row.mobileDevice.name, {
      id: 'device',
      header: 'Device',
      enableSorting: true,
    }),
    columnHelper.accessor((row) => row.mobileDevice.application, {
      id: 'application',
      header: 'Application',
      enableSorting: true,
    }),
    columnHelper.display({
      id: 'view',
      header: '',
      cell: (info) => {
        const device = info.row.original;
        return (
          <Button
            variant="secondary"
            size="sm"
            as={Link}
            to={`/mobile-device/view/${device.mobileDevice.id}`}>
            View
          </Button>
        );
      },
    }),
  ], [columnHelper]);

  const devices = devicesResult.result?.data || [];

  return (
    <div className="jarviswidget">
      <div className="row">
        <div className="col-md-12 mb-3">
          <Row className="mb-3">
            <Col md={3}>
              <InmateDeviceBlacklistAdd
                inmate={inmate}
                onBlacklistChange={onBlacklistChange} />
            </Col>
          </Row>
          <Card>
            <Card.Header>
              <h5>Device History</h5>
            </Card.Header>
            <Card.Body>
              <BaseHtmlTable
                rows={devices}
                columnDefs={columns}
                paginationMode="client"
                perPage={100}
                loading={devicesResult.loading} />
            </Card.Body>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default InmateDevices;
